/*
 *
 * Appointments
 *
 */

import "./psychologists.scss";

import { API, Endpoints } from "utils/api";
import { Calendar, Icons } from "components";
import { Col, Input, Modal, Row, Select, notification } from "antd";
import { setBreadcrumb, setLoader, setRoom, setVideoSettings } from "store/actions";

import Animation from "assets/images/lottie/calendar.json";
import { DateTime } from "luxon";
import { Footer } from "screens";
import { Helmet } from "react-helmet";
import React from "react";
import Strings from "utils/strings";
import { UserPlaceholder } from "utils/utils";
import { connect } from "react-redux";
import lottie from "lottie-web";
import moment from "moment";
import { push } from "connected-react-router";

export class Appointments extends React.Component<any, any> {
	ref: any;
	debounceTimer: any = null;

	constructor(props: any) {
		super(props);

		if (this.props.language === "pt") {
			moment.updateLocale("pt", {
				week: {
					dow: 1 /// Date offset
				}
			});
		} else {
			moment.updateLocale("en", {
				week: {
					dow: 1 /// Date offset
				}
			});
		}

		const diff = DateTime.utc().weekday - 1;
		const startDate = moment.utc().subtract(diff, "days");
		const endDate = moment.utc().add(7 - (diff + 1), "days");

		const filters = {
			startDate,
			endDate
		};

		this.state = {
			consultations: [],
			availability: [],
			defaultPsychologists: [],
			showFilters: true,
			filters,
			selectedDate: DateTime.utc().toFormat("dd/MM/yyyy"),
			showAppointmentModal: false,
			selectedAppointment: null,
			selectedPsychologist: null,
			showSlotOptionsModal: false,
			selectedSlot: null,
			selectedSlotOption: null,
			slotUsers: [],
			searchText: "",
			selectedUser: null,
			slotMotive: ""
		};
	}

	async componentDidMount() {
		const { dispatch } = this.props;
		dispatch(
			setBreadcrumb(() => ({
				locations: [
					{
						text: Strings.appointments.header,
						route: "/appointments"
					}
				]
			}))
		);

		await this.getPsychologists();
		await this.getData();
		await this.getUsers(); // Preload users for the consultation option
	}

	componentDidUpdate(prevProps: any) {
		if (this.props.language === "pt") {
			moment.updateLocale("pt", {
				week: {
					dow: 1 /// Date offset
				}
			});
		} else {
			moment.updateLocale("en", {
				week: {
					dow: 1 /// Date offset
				}
			});
		}
	}

	async getData() {
		const { filters, selectedPsychologist } = this.state;
		const { dispatch, user } = this.props;

		dispatch(setLoader(true));

		let response: any;
		try {
			response = await API.get({
				url: Endpoints.uriCalendar(
					moment(filters.startDate).format("YYYY-MM-DD"),
					moment(filters.endDate).format("YYYY-MM-DD"),
					user?.isAdmin ? selectedPsychologist : ""
				)
			});

			if (response.ok) {
				const { consultations = [], schedules = [] } = response.data.results;
				this.setState({ consultations, availability: schedules });
			}
		} catch (err) {
			notification.error({
				message: Strings.appointments.header,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async getPsychologists() {
		const { dispatch, user } = this.props;

		if (!user.isAdmin) {
			this.setState({
				defaultPsychologists: [user],
				selectedPsychologist: user?._id || null
			});
			return;
		}

		dispatch(setLoader(true));

		let response: any;
		try {
			response = await API.get({
				url: Endpoints.uriProfile()
			});

			if (response.ok) {
				const { psychologist: defaultPsychologists = [] } = response.data.results;
				this.setState({ defaultPsychologists, selectedPsychologist: user?._id || null });
			}
		} catch (err) {
			notification.error({
				message: Strings.appointments.header,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	loadAnimation() {
		lottie.loadAnimation({
			container: this.ref,
			renderer: "svg",
			loop: true,
			autoplay: true,
			animationData: Animation
		});
	}

	disabledRange = (current: any) => {
		return moment.utc().isBefore(current);
	};

	startVideoCall = async (id: any, user?: any) => {
		const { dispatch, room, audioRoom } = this.props;

		dispatch(setLoader(true));

		if (room || audioRoom) {
			return notification.warn({
				message: Strings.home.pendingCalls,
				description: Strings.home.cantAcceptCall,
				placement: "bottomRight",
				duration: 5
			});
		}

		const response = await API.get({
			url: Endpoints.uriVideoCalls(`consultation/${id}`)
		});

		if (response.ok) {
			const { twilioRoom, userToken } = response.data.results;

			this.setState(
				{
					activeRoom: twilioRoom.roomSid,
					roomName: twilioRoom.uniqueName,
					room: twilioRoom,
					roomState: "disconnected",
					roomNameErr: false,
					previewTracks: null,
					token: userToken,
					localMediaAvailable: true,
					hasJoinedRoom: true,
					openVideoCallModal: true
				},
				() => {
					const room = { ...twilioRoom, user };
					dispatch(setRoom(room));
					dispatch(setVideoSettings({ token: userToken, activeVideo: true, room, appointmentId: id }));
				}
			);
		} else {
			this.setState({ roomNameErr: true });

			return notification.error({
				message: Strings.serverErrors.title,
				description: Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	};

	async saveReport() {
		const { selectedAppointment } = this.state;
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		let response: any;
		try {
			response = await API.put({
				url: Endpoints.uriAppointments(`${selectedAppointment?._id}/report`),
				data: {
					clinicalIntervention: selectedAppointment?.clinicalIntervention,
					clinicalRecord: selectedAppointment?.clinicalRecord,
					goalsNextConsultation: selectedAppointment.goalsNextConsultation
				}
			});

			if (response.ok) {
				await this.getData();
				notification.success({
					message: Strings.sidebar.appointments,
					description: Strings.appointments.edited,
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.sidebar.appointments,
					description: response?.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.sidebar.appointments,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async createAppointment() {
		const { selectedSlot, selectedSlotOption, selectedUser, slotMotive } = this.state;
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		// Parse the slot's dateTime to create the appointment
		const appointmentDateTime = DateTime.fromISO(selectedSlot.dateTime);

		// Build the appointment data
		const appointmentData = {
			date: appointmentDateTime.toFormat("yyyy-MM-dd"),
			time: appointmentDateTime.toFormat("HH:mm"),
			duration: 45,
			type: selectedSlotOption,
			user: selectedSlotOption === "consultation" ? selectedUser : null,
			psychologist: this.props.user?._id,
			motive: selectedSlotOption === "other" ? slotMotive : null
		};

		let response: any;
		try {
			// This endpoint is a placeholder - update with your actual appointment creation endpoint
			response = await API.post({
				url: Endpoints.uriAppointments(),
				data: appointmentData
			});

			if (response.ok) {
				notification.success({
					message: Strings.appointments.header,
					description: Strings.appointments.created,
					placement: "bottomRight",
					duration: 5
				});

				// Close the modal and refresh the calendar
				this.setState({
					showSlotOptionsModal: false,
					selectedSlot: null,
					selectedSlotOption: null,
					selectedUser: null,
					slotMotive: ""
				});

				// Refresh calendar data
				await this.getData();
			} else {
				notification.error({
					message: Strings.appointments.header,
					description: response?.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.appointments.header,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	handleSlotUserSearch = (value: string) => {
		if (this.debounceTimer) clearTimeout(this.debounceTimer);
		this.debounceTimer = setTimeout(() => {
			this.setState({ searchText: value }, () => this.getUsers());
		}, 750);
	};

	async getUsers() {
		const { searchText } = this.state;
		const { dispatch } = this.props;

		let response: any;
		try {
			response = await API.get({
				url: Endpoints.uriPatients(),
				params: {
					page: 0,
					perPage: 100,
					search: searchText?.trim() || undefined
				}
			});
			if (response.ok) {
				const slotUsers = response.data?.results?.patients || [];
				this.setState({ slotUsers });
			}
		} catch (err) {
			notification.error({
				message: Strings.appointments.header,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	renderCalendar() {
		const { consultations, availability, selectedDate, selectedPsychologist } = this.state;

		const { dispatch, user } = this.props;

		return (
			<Calendar
				hasDate
				date={selectedDate}
				schedule={consultations}
				availability={availability}
				readOnly={(selectedPsychologist && selectedPsychologist !== user?._id) || false}
				onDateChange={(date: any, update: boolean) => {
					if (update) {
						const diff = DateTime.fromFormat(date, "dd/MM/yyyy").weekday - 1;
						const startDate = moment(date, "DD/MM/YYYY").subtract(diff, "days");
						const endDate = moment(date, "DD/MM/YYYY").add(7 - (diff + 1), "days");

						this.setState({ selectedDate: date, filters: { startDate, endDate } }, () => this.getData());
					} else {
						this.setState({ selectedDate: date });
					}
				}}
				onReportClick={(appointment: any) => {
					dispatch(push(`/appointments/${appointment?._id}/report`));
				}}
				onVideoClick={(appointment: any) => this.startVideoCall(appointment._id, appointment.user)}
				onClick={(cell: any) => {
					this.setState({
						showAppointmentModal: true,
						selectedAppointment: JSON.parse(JSON.stringify(cell))
					});
				}}
				onSlotSelect={(slot: any) => {
					if (selectedPsychologist && selectedPsychologist === user?._id) {
						this.setState({
							showSlotOptionsModal: true,
							selectedSlot: slot,
							selectedSlotOption: "consultation", // Set default to consultation
							selectedUser: null,
							slotMotive: ""
						});
					}
				}}
			/>
		);
	}

	renderModal() {
		const { showAppointmentModal, selectedAppointment, selectedPsychologist } = this.state;
		const { mobile, dispatch, user } = this.props;

		return (
			<Modal
				centered={true}
				closable={true}
				visible={showAppointmentModal}
				className={`CalendarAppointmentDetails${selectedAppointment?.user ? " __with-user" : " __without-user"}`}
				footer={null}
				width={mobile ? "100%" : selectedAppointment?.user ? 700 : 500}
				onCancel={() => this.setState({ showAppointmentModal: false, selectedAppointment: null })}
			>
				{selectedAppointment?.user ? (
					<React.Fragment>
						<h2>
							{Strings.appointments.single} - {selectedAppointment?.user?.name}
						</h2>
						<div className="CalendarAppointmentInfo">
							<Row gutter={[20, 10]}>
								<Col xs={24} md={6}>
									<div className="CalendarAppointmentPatientAvatar">
										<img
											src={selectedAppointment?.user?.photo || UserPlaceholder}
											alt="patient avatar"
										/>
										<button
											onClick={() =>
												dispatch(push(`/patients/${selectedAppointment?.user?._id}`))
											}
										>
											<Icons.Eye />
											<p>{Strings.appointments.checkPatient}</p>
										</button>
									</div>
								</Col>
								<Col xs={24} md={18}>
									<Row gutter={[0, 10]}>
										<Col xs={24} md={12}>
											<Row gutter={[0, 10]}>
												<Col xs={24}>
													<label htmlFor="consultation_name" className="InputLabel">
														{Strings.patients.patient}
													</label>
													<p id="consultation_name">{selectedAppointment?.user?.name}</p>
												</Col>
												<Col xs={24}>
													<label htmlFor="consultation_client" className="InputLabel">
														{Strings.appointments.client}
													</label>
													<p id="consultation_client">
														{selectedAppointment?.business?.name || "-"}
													</p>
												</Col>
											</Row>
										</Col>
										<Col xs={24} md={12}>
											<Row gutter={[0, 10]}>
												<Col xs={24}>
													<label htmlFor="consultation_date" className="InputLabel">
														{Strings.reports.date}
													</label>
													<p id="consultation_date">
														{DateTime.fromISO(selectedAppointment?.startDate).toFormat(
															"dd/MM/yyyy - HH:mm"
														)}
													</p>
												</Col>
												<Col xs={24}>
													<label htmlFor="consultation_duration" className="InputLabel">
														{Strings.appointments.duration}
													</label>
													<p id="consultation_duration">
														{selectedAppointment?.duration} min
													</p>
												</Col>
											</Row>
										</Col>
									</Row>
								</Col>
							</Row>
							{(selectedAppointment?.finished && selectedPsychologist !== user?._id && (
								<div className="CalendarReportContainer">
									<h3>{Strings.reports.header}</h3>
									<Row gutter={[0, 10]}>
										<Col xs={24}>
											<label htmlFor="report_record" className="InputLabel">
												{Strings.reports.report}
											</label>
											<Input.TextArea
												id="report_record"
												value={selectedAppointment?.clinicalRecord || ""}
												className="TreatmentTextarea"
												rows={4}
												placeholder={Strings.reports.report}
												onChange={(e: any) => {
													const value = e.target.value;
													this.setState((prevState: any) => ({
														selectedAppointment: {
															...prevState.selectedAppointment,
															clinicalRecord: value
														}
													}));
												}}
											/>
										</Col>
										<Col xs={24}>
											<label htmlFor="report_intervention" className="InputLabel">
												{Strings.reports.intervention}
											</label>
											<Input.TextArea
												id="report_intervention"
												value={selectedAppointment?.clinicalIntervention || ""}
												className="TreatmentTextarea"
												rows={4}
												placeholder={Strings.reports.intervention}
												onChange={(e: any) => {
													const value = e.target.value;
													this.setState((prevState: any) => ({
														selectedAppointment: {
															...prevState.selectedAppointment,
															clinicalIntervention: value
														}
													}));
												}}
											/>
										</Col>
										<Col xs={24}>
											<label htmlFor="report_objectives" className="InputLabel">
												{Strings.reports.objectivesNextAppointment}
											</label>
											<Input.TextArea
												id="report_objectives"
												value={selectedAppointment?.goalsNextConsultation || ""}
												className="TreatmentTextarea"
												rows={4}
												placeholder={Strings.reports.objectivesNextAppointment}
												onChange={(e: any) => {
													const value = e.target.value;
													this.setState((prevState: any) => ({
														selectedAppointment: {
															...prevState.selectedAppointment,
															goalsNextConsultation: value
														}
													}));
												}}
											/>
										</Col>
										<Col xs={24}>
											<div className="SubmitReport">
												<button onClick={() => this.saveReport()}>
													{Strings.reports.save}
												</button>
											</div>
										</Col>
									</Row>
								</div>
							)) ||
								null}
						</div>
					</React.Fragment>
				) : (
					<React.Fragment>
						<h2>{selectedAppointment?.title}</h2>
						<div className="SimpleAppointmentInfo AppointmentsInfoTopMargin">
							<Row gutter={[0, 20]}>
								<Col xs={24}>
									<div className="AppointmentDetails">
										<p>
											<strong>{Strings.appointments.date}:</strong>{" "}
											{DateTime.fromISO(selectedAppointment?.startDate).toFormat(
												"dd/MM/yyyy - HH:mm"
											)}
										</p>
										<p>
											<strong>{Strings.appointments.duration}:</strong>{" "}
											{selectedAppointment?.duration} min
										</p>
										{selectedAppointment?.motive && (
											<p>
												<strong>{Strings.appointments.motive}:</strong>{" "}
												{selectedAppointment.motive}
											</p>
										)}
									</div>
								</Col>
								{selectedPsychologist === user?._id && (
									<Col xs={24}>
										<div className="AppointmentActions">
											<button className="CancelButton" onClick={() => this.cancelAppointment()}>
												{Strings.appointments.cancel}
											</button>
										</div>
									</Col>
								)}
							</Row>
						</div>
					</React.Fragment>
				)}
			</Modal>
		);
	}

	renderSlotOptionsModal() {
		const {
			showSlotOptionsModal,
			selectedSlot,
			selectedSlotOption,
			slotUsers,
			selectedUser,
			slotMotive,
			searchText
		} = this.state;
		const { mobile } = this.props;

		return (
			<Modal
				centered={true}
				closable={true}
				visible={showSlotOptionsModal}
				className="CalendarSlotOptions AppointmentsSlotOptionsModal"
				footer={null}
				width={mobile ? "100%" : 500}
				onCancel={() =>
					this.setState({
						showSlotOptionsModal: false,
						selectedSlot: null,
						selectedSlotOption: null,
						selectedUser: null,
						slotMotive: "",
						searchText: ""
					})
				}
			>
				<h2>{Strings.appointments.scheduleOptions}</h2>
				<div className="CalendarSlotOptionsContent">
					<Row gutter={[0, 20]}>
						<Col xs={24}>
							<div className="SlotTimeInfo">
								<p>
									<strong>{Strings.appointments.date}:</strong> {selectedSlot?.date}
								</p>
								<p>
									<strong>{Strings.appointments.hour}:</strong> {selectedSlot?.hour}
								</p>
							</div>
						</Col>
						<Col xs={24}>
							<label htmlFor="slot_option" className="InputLabel">
								{Strings.appointments.scheduleType}
							</label>
							<Select
								id="slot_option"
								className="AppointmentsFullWidth"
								placeholder={Strings.appointments.scheduleType}
								value={selectedSlotOption}
								onChange={(value) => this.setState({ selectedSlotOption: value })}
							>
								<Select.Option value="consultation">
									{Strings.appointments.optionConsultation}
								</Select.Option>
								<Select.Option value="other">{Strings.appointments.optionOther}</Select.Option>
							</Select>
						</Col>

						{selectedSlotOption === "consultation" && (
							<Col xs={24}>
								<label htmlFor="slot_user" className="InputLabel">
									{Strings.appointments.selectPatient}
								</label>
								<Select
									id="slot_user"
									className="AppointmentsFullWidth"
									showSearch
									value={selectedUser}
									onChange={(value) => this.setState({ selectedUser: value })}
									onSearch={this.handleSlotUserSearch}
									filterOption={false}
								>
									{slotUsers?.map((user: any) => (
										<Select.Option key={user._id} value={user._id}>
											{user.name}
										</Select.Option>
									))}
								</Select>
							</Col>
						)}

						{selectedSlotOption === "other" && (
							<Col xs={24}>
								<label htmlFor="slot_motive" className="InputLabel">
									{Strings.appointments.motive}
								</label>
								<Input.TextArea
									id="slot_motive"
									rows={4}
									value={slotMotive}
									onChange={(e) => this.setState({ slotMotive: e.target.value })}
								/>
							</Col>
						)}

						<Col xs={24}>
							<div className="SlotOptionsFooter">
								<button
									className="CancelButton"
									onClick={() =>
										this.setState({
											showSlotOptionsModal: false,
											selectedSlot: null,
											selectedSlotOption: null,
											selectedUser: null,
											slotMotive: ""
										})
									}
								>
									{Strings.appointments.cancel}
								</button>
								<button
									className="ScheduleButton"
									onClick={() => this.createAppointment()}
									disabled={
										(selectedSlotOption === "consultation" && !selectedUser) ||
										(selectedSlotOption === "other" && !slotMotive.trim())
									}
								>
									{Strings.appointments.schedule}
								</button>
							</div>
						</Col>
					</Row>
				</div>
			</Modal>
		);
	}

	async cancelAppointment() {
		const { selectedAppointment } = this.state;
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		let response: any;
		try {
			response = await API.patch({
				url: Endpoints.uriCancelAppointment(selectedAppointment?._id),
				data: {}
			});

			if (response.ok) {
				notification.success({
					message: Strings.appointments.header,
					description: Strings.appointments.cancelled || "Appointment cancelled successfully",
					placement: "bottomRight",
					duration: 5
				});

				// Close the modal and refresh the calendar
				this.setState({
					showAppointmentModal: false,
					selectedAppointment: null
				});

				// Refresh calendar data
				await this.getData();
			} else {
				notification.error({
					message: Strings.appointments.header,
					description: response?.data?.message || Strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: Strings.appointments.header,
				description: response?.data?.message || Strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	render() {
		const { selectedPsychologist, defaultPsychologists } = this.state;
		const { mobile, user } = this.props;

		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.sidebar.appointments} | Team24</title>
					<meta name="description" content="Description of Appointments" />
				</Helmet>
				<div className="CalendarContent">
					<div className="AppointmentsMainWrapper">
						<h1 className="GenericTitle">{Strings.appointments.header}</h1>
						{(user?.isAdmin && (
							<div className="CalendarPsychologistSelect">
								<label htmlFor="calendar_psychologist" className="InputLabel">
									{Strings.profile.psychologist}
								</label>
								<Select
									id="calendar_psychologist"
									className="CalendarPsychologistSelectInput"
									placeholder={Strings.appointments.selectPsychologist}
									showSearch
									allowClear={false}
									filterOption={(input: any, option: any) =>
										option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
									}
									value={selectedPsychologist || null}
									onChange={(selectedPsychologist: any) =>
										this.setState({ selectedPsychologist }, () => this.getData())
									}
								>
									{defaultPsychologists
										.sort((a: any, b: any) => a.externalName?.localeCompare(b.externalName))
										.map((psychologist: any) => (
											<Select.Option
												key={`psychologist_${psychologist._id}`}
												value={psychologist._id}
											>
												{psychologist.externalName || psychologist.name}
											</Select.Option>
										))}
								</Select>
							</div>
						)) ||
							null}
						{this.renderCalendar()}
					</div>
					{mobile && <Footer />}
				</div>
				{this.renderModal()}
				{this.renderSlotOptionsModal()}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	user: state.user,
	mobile: state.mobile,
	room: state.room,
	audioRoom: state.audioRoom
});

export default connect(mapStateToProps)(Appointments);
